import { Container, Rectangle, Sprite } from 'pixi.js';
import { Signal } from 'typed-signals';
import { MainMenuPageSelectorView } from './MainMenuPageSelectorView';
import { registerResizableObject } from '../utils/pixi/registerResizableObject';
import { MainMenuTopBarView } from './MainMenuTopBarView';
import type { PageType } from './types';
import type { AppStateController } from '../app/state/AppStateController';
import type { CoinCounterView } from '../game/views/common/counters/CoinCounterView';
import { getBannerHeight, getSafeAreaInset } from '../safearea/SafeAreaProvider';

export type MenuPage = Container & {
  show(animationPromise: Promise<void>): void;
  hide(animationPromise: Promise<void>): void;
  isAboveTopBar(): boolean;
  resize(width: number, height: number, screenRect: Rectangle): void;
};

type PageConfig = {
  page: MenuPage | null,
  type: PageType,
  locked: boolean,
  hasNotification: boolean,
  onPageButtonClicked?: () => void,
};

export type MainMenuViewConfig = {
  state: AppStateController;
  iapAvailable: boolean;
  pages: PageConfig[];
  activePage: PageType;
};

export class MainMenuView extends Container {
  public readonly onTabButtonClicked = new Signal<(tab: PageType) => void>();

  private readonly background = new Sprite().props({
    texture: 'Wood_Background',
    parent: this,
    eventMode: 'static',
  });

  public readonly topBar: MainMenuTopBarView;

  private readonly pagesContainer = new Container().props({
    parent: this,
    y: 100,
  });

  private readonly selector: MainMenuPageSelectorView;

  private readonly currentPageSize = { width: 780, height: 1280 };

  private readonly pages: Partial<Record<PageType, MenuPage | null>> = {};

  private readonly pagesInOrder: MenuPage[] = [];

  private currentPage: PageType;

  constructor(private readonly config: MainMenuViewConfig) {
    super();

    this.selector = new MainMenuPageSelectorView({
      buttons: config.pages,
      activeButton: config.activePage,
    }).props({
      parent: this,
    });
    this.selector.onTabButtonClicked.connect((tab) => {
      if (tab === this.currentPage) return;
      this.onTabButtonClicked.emit(tab);
    });

    this.topBar = new MainMenuTopBarView(config.state, config.iapAvailable).props({
      parent: this,
    });

    config.pages.forEach(({ type, page }) => {
      this.pages[type] = page;
      if (!page) return;

      this.pagesInOrder.push(page);
      page.props({
        parent: this.pagesContainer,
        y: page.isAboveTopBar() ? -this.pagesContainer.y : 0,
      });
    });

    this.pages[config.activePage]?.show(Promise.resolve());
    this.currentPage = config.activePage;

    this.tweenFrom({ alpha: 0 }, 300, 'quadOut', true);

    registerResizableObject(this);
  }

  public async hideUI(): Promise<void> {
    await Promise.all([this.topBar.hide(), this.selector.hide()]);
  }

  public async showUI(): Promise<void> {
    const showTopBar = !this.pages[this.currentPage]?.isAboveTopBar();
    await Promise.all([this.selector.show(), showTopBar ? this.topBar.show() : Promise.resolve()]);
  }

  public selectPage(page: PageType): void {
    if (this.currentPage === page) return;

    const pageConfig = this.config.pages.find((config) => config.type === page);
    pageConfig?.onPageButtonClicked?.();

    if (!pageConfig?.page) {
      // no page is defined for this config, example: 'Settings button'.
      return;
    }

    const prevPage = this.currentPage;
    this.currentPage = page;
    this.selector.setActiveButton(page);

    if (this.pages[page]?.isAboveTopBar()) {
      void this.topBar.hide();
    } else {
      void this.topBar.show();
    }

    const animationPromise = this.pagesContainer
      .tweenTo({ x: -(this.pages[page]?.x ?? 0) }, 300, 'quadOut', true)
      .asyncOnComplete();

    this.pages[page]?.show(animationPromise);
    this.pages[prevPage]?.hide(animationPromise);

    this.callPageResize();
  }

  public getCurrentPage(): PageType {
    return this.currentPage;
  }

  public toggleNotificationOnPage(page: PageType, value: boolean): void {
    this.selector.toggleNotificationOnButton(page, value);
  }

  public resize(width: number, height: number): void {
    const { state } = this.config;
    const isHorizontal = state.isHorizontalLayoutActive();
    const safeArea = getSafeAreaInset();
    const bannerHeight = getBannerHeight();

    this.background.props({ width, height });

    this.pagesInOrder.forEach((page, index) => {
      page.x = index * width;
    });

    this.pagesContainer.pivot.x = width * -0.5;
    this.pagesContainer.x = -(this.pages[this.currentPage]?.x ?? 0);
    this.pagesContainer.y = isHorizontal ? 200 + safeArea.top : 100 + safeArea.top;

    const topAndBottomBarSize = this.pagesContainer.y + (isHorizontal ? 80 : 240);

    this.currentPageSize.width = width - safeArea.left - safeArea.right;
    this.currentPageSize.height = height - topAndBottomBarSize - safeArea.bottom;

    this.callPageResize();

    this.selector.setHorizontalLayout(isHorizontal);
    this.selector.props(
      isHorizontal
        ? {
            x: width - safeArea.right,
            y: safeArea.top,
          }
        : {
            x: width * 0.5,
            y: height - safeArea.bottom - bannerHeight,
          },
    );
  }

  private callPageResize(): void {
    const pageView = this.pages[this.currentPage];
    if (!pageView) return;

    const screenRect = new Rectangle(
      this.pagesContainer.pivotX,
      -this.pagesContainer.y,
      this.pixi.renderer.width,
      this.pixi.renderer.height,
    );

    if (pageView.isAboveTopBar()) {
      pageView.resize(this.currentPageSize.width, this.currentPageSize.height + this.pagesContainer.y, screenRect);
    } else {
      pageView.resize(this.currentPageSize.width, this.currentPageSize.height, screenRect);
    }
  }

  public async close(): Promise<void> {
    await this.tweenTo({ alpha: 0 }, 300, 'quadOut', true).asyncOnComplete();
    super.destroy({ children: true });
  }

  public getCoinCounter(): CoinCounterView {
    return this.topBar.moneyCounter;
  }
}
